import React, { useState } from 'react';
import { Plus, Trash2, X, Edit2, Trophy, Calendar, Activity, Dumbbell, Bike, Waves, Footprints, ChevronLeft, FileText, Star, Settings } from 'lucide-react';
import { useConfirm } from '../lib/ConfirmDialog';
import { useTranslation } from '../lib/i18n';

const Fitness = ({ data, updateData, viewMode }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [showAddSport, setShowAddSport] = useState(false);
  const [editingSport, setEditingSport] = useState(null);
  const [selectedSport, setSelectedSport] = useState(null);
  const [viewingSportDetail, setViewingSportDetail] = useState(null);
  const [showAddCrossfitRecord, setShowAddCrossfitRecord] = useState(false);
  const [newCrossfitRecord, setNewCrossfitRecord] = useState({
    exercise: '',
    weight: '',
    reps: '',
    time: '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [editingCrossfitRecord, setEditingCrossfitRecord] = useState(null);

  // Workout structure manager
  const [showWorkoutStructureManager, setShowWorkoutStructureManager] = useState(false);
  const [activeWorkoutTab, setActiveWorkoutTab] = useState(() => {
    return localStorage.getItem('workout_structure_last_tab') || 'warmup';
  });
  const [newExerciseInput, setNewExerciseInput] = useState('');

  // Workout structure storage (persisted via data.fitness)
  const workoutStructure = data.fitness?.workoutStructure || {
    warmup: [],
    strength: [],
    workoutOfMonth: [],
    wod: []
  };

  // Crossfit personal bests storage (persisted via data.fitness)
  const crossfitRecords = data.fitness?.crossfitRecords || {};

  // Which sport page we're currently on (defaults to Crossfit for dashboard/overview)
  const currentSportName = viewingSportDetail?.name || 'Crossfit';

  // Per-sport workouts: { Crossfit: [...], Zumba: [...] }
  // Migrates legacy `crossfitWorkouts` field.
  const workoutsBySport = data.fitness?.workoutsBySport
    || (data.fitness?.crossfitWorkouts ? { Crossfit: data.fitness.crossfitWorkouts } : {});

  // Per-sport calendar-label links
  const calendarLinksBySport = data.fitness?.calendarLinksBySport
    || (data.fitness?.crossfitCalendarCategoryId
      ? { Crossfit: data.fitness.crossfitCalendarCategoryId }
      : {});

  const currentWorkouts = workoutsBySport[currentSportName] || [];
  const currentCalendarCategoryId = calendarLinksBySport[currentSportName] || '';
  const [showCalendarLinker, setShowCalendarLinker] = useState(false);

  // Persist the current sport's workouts (+ optional extra fitness fields / calendar update)
  const persistCurrentWorkouts = (newList, { extraFitness = {}, calendar = null } = {}) => {
    const newMap = { ...workoutsBySport, [currentSportName]: newList };
    const fitnessUpdate = {
      ...data.fitness,
      workoutsBySport: newMap,
      ...extraFitness
    };
    // Keep legacy field synced for Crossfit for backward compatibility
    if (currentSportName === 'Crossfit') {
      fitnessUpdate.crossfitWorkouts = newList;
    }
    const payload = { fitness: fitnessUpdate };
    if (calendar) payload.calendar = calendar;
    updateData(payload);
  };

  // Build a calendar event from a workout + chosen category
  // Title = sport name (Crossfit/Zumba), label is just color/grouping
  const buildCalendarEventForWorkout = (workout, categoryId, sportName) => {
    let endTime = '';
    if (workout.startTime && workout.duration) {
      const [h, m] = workout.startTime.split(':').map(Number);
      const totalMin = h * 60 + m + parseInt(workout.duration, 10);
      const endH = Math.floor(totalMin / 60) % 24;
      const endM = totalMin % 60;
      endTime = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
    }
    return {
      id: `wk-${sportName}-${workout.id}`,
      title: sportName,
      date: workout.date,
      endDate: workout.date,
      startTime: workout.startTime || '',
      endTime,
      allDay: !workout.startTime,
      categoryId,
      notes: workout.notes || '',
      linkedWorkoutId: workout.id,
      linkedSportName: sportName
    };
  };

  // Rebuild calendar events for the current sport's workouts when (un)linking.
  const rebuildSportCalendarEvents = (workouts, categoryId, sportName) => {
    const allEvents = data.calendar?.events || [];
    // Drop events linked to THIS sport; keep events of other sports
    const filteredEvents = allEvents.filter((ev) => {
      if (ev.linkedSportName) return ev.linkedSportName !== sportName;
      // Legacy: events created before `linkedSportName` existed were Crossfit-only
      if (String(ev.id).startsWith('wk-') && !String(ev.id).startsWith(`wk-${sportName}-`)) {
        return sportName !== 'Crossfit';
      }
      return true;
    });

    if (!categoryId) {
      const strippedWorkouts = workouts.map((w) => {
        const { linkedCalendarEventId, ...rest } = w;
        return rest;
      });
      return { updatedWorkouts: strippedWorkouts, updatedEvents: filteredEvents };
    }

    const newEvents = [];
    const updatedWorkouts = workouts.map((w) => {
      const event = buildCalendarEventForWorkout(w, categoryId, sportName);
      newEvents.push(event);
      return { ...w, linkedCalendarEventId: event.id };
    });
    return {
      updatedWorkouts,
      updatedEvents: [...filteredEvents, ...newEvents]
    };
  };

  // Apply linking for current sport: persist sport-level label and rebuild events
  const setCurrentCalendarLink = (categoryId) => {
    const { updatedWorkouts, updatedEvents } = rebuildSportCalendarEvents(
      currentWorkouts,
      categoryId,
      currentSportName
    );
    const newWorkoutsMap = { ...workoutsBySport, [currentSportName]: updatedWorkouts };
    const newLinksMap = { ...calendarLinksBySport, [currentSportName]: categoryId || '' };

    const fitnessUpdate = {
      ...data.fitness,
      workoutsBySport: newWorkoutsMap,
      calendarLinksBySport: newLinksMap
    };
    if (currentSportName === 'Crossfit') {
      fitnessUpdate.crossfitWorkouts = updatedWorkouts;
      fitnessUpdate.crossfitCalendarCategoryId = categoryId || '';
    }

    updateData({
      fitness: fitnessUpdate,
      calendar: { ...data.calendar, events: updatedEvents }
    });
  };

  const [showAddWorkout, setShowAddWorkout] = useState(false);
  const [newWorkout, setNewWorkout] = useState({
    date: new Date().toISOString().split('T')[0],
    startTime: '',
    duration: '60',
    notes: '',
    addToCalendar: false,
    calendarCategoryId: ''
  });
  const [workoutRecords, setWorkoutRecords] = useState([]);

  // Close + fully reset the Log Workout modal (shared by save, cancel and X button)
  const closeAddWorkoutModal = () => {
    setShowAddWorkout(false);
    setWorkoutRecords([]);
    setNewWorkout({
      date: new Date().toISOString().split('T')[0],
      startTime: '',
      duration: '60',
      notes: '',
      addToCalendar: false,
      calendarCategoryId: '',
      warmup: '',
      warmupDetails: '',
      strength: '',
      strengthDetails: '',
      workoutOfMonth: '',
      workoutOfMonthDetails: '',
      wod: '',
      wodDetails: '',
      wodType: '',
      wodResult: ''
    });
  };
  const [showAddWorkoutRecord, setShowAddWorkoutRecord] = useState(false);
  const [newWorkoutRecord, setNewWorkoutRecord] = useState({
    exercise: '',
    weight: '',
    reps: '',
    time: '',
    notes: ''
  });
  const [viewingWorkout, setViewingWorkout] = useState(null);

  // Helper: persist a partial update to the currently-viewed workout + sync its calendar event
  const updateViewingWorkout = (patch) => {
    if (!viewingWorkout) return;
    const updatedWorkout = { ...viewingWorkout, ...patch };
    const updatedWorkouts = currentWorkouts.map((w) =>
      w.id === viewingWorkout.id ? updatedWorkout : w
    );
    setViewingWorkout(updatedWorkout);

    let calendarPayload = null;
    if (viewingWorkout.linkedCalendarEventId && currentCalendarCategoryId) {
      const cat = (data.calendar?.categories || []).find(
        (c) => c.id === currentCalendarCategoryId
      );
      if (cat) {
        const newEvent = buildCalendarEventForWorkout(
          updatedWorkout,
          currentCalendarCategoryId,
          currentSportName
        );
        calendarPayload = {
          ...data.calendar,
          events: (data.calendar?.events || []).map((ev) =>
            ev.id === viewingWorkout.linkedCalendarEventId ? newEvent : ev
          )
        };
      }
    }
    persistCurrentWorkouts(updatedWorkouts, { calendar: calendarPayload });
  };

  // Crossfit-specific exercises
  const crossfitExercises = {
    lifts: [
      'Back Squat',
      'Front Squat',
      'Overhead Squat',
      'Deadlift',
      'Bench Press',
      'Strict Press',
      'Push Press',
      'Push Jerk',
      'Power Clean'
    ],
    gymnastics: [
      'Pull-ups',
      'Handstand Push ups',
      'Push-ups',
      'Handstand Hold',
      'Handstand Walk'
    ]
  };
  const [newSport, setNewSport] = useState({ name: '', startDate: '' });

  // Predefined sports with their default units and icons
  const predefinedSports = [
    { id: 'crossfit', name: 'Crossfit', unit: '', icon: 'dumbbell' },
    { id: 'running', name: 'Running', unit: 'km', icon: 'run' },
    { id: 'walking', name: 'Walking', unit: 'km', icon: 'run' },
    { id: 'gym', name: 'Gym', unit: '', icon: 'dumbbell' },
    { id: 'iceskating', name: 'Iceskating', unit: 'min', icon: 'activity' },
    { id: 'football', name: 'Football', unit: 'min', icon: 'activity' },
    { id: 'zumba', name: 'Zumba', unit: 'min', icon: 'activity' }
  ];
  const sports = data.fitness?.sports || [];
  const personalBests = data.fitness?.personalBests || [];

  const getSportIcon = (iconName) => {
    switch (iconName) {
      case 'dumbbell': return Dumbbell;
      case 'bike': return Bike;
      case 'swim': return Waves;
      case 'run': return Footprints;
      default: return Activity;
    }
  };

  // Calculate duration from start date to now
  const calculateDuration = (startDate) => {
    if (!startDate) return null;
    const start = new Date(startDate);
    const now = new Date();
    
    let years = now.getFullYear() - start.getFullYear();
    let months = now.getMonth() - start.getMonth();
    let days = now.getDate() - start.getDate();
    
    if (days < 0) {
      months--;
      days += new Date(now.getFullYear(), now.getMonth(), 0).getDate();
    }
    if (months < 0) {
      years--;
      months += 12;
    }
    
    const parts = [];
    if (years > 0) parts.push(years > 1 ? t('fitness.yearsMany', { n: years }) : t('fitness.yearsOne', { n: years }));
    if (months > 0) parts.push(months > 1 ? t('fitness.monthsMany', { n: months }) : t('fitness.monthsOne', { n: months }));
    if (days > 0) parts.push(days > 1 ? t('fitness.daysMany', { n: days }) : t('fitness.daysOne', { n: days }));
    
    return parts.join(', ');
  };

  // Workout structure management
  const handleWorkoutTabChange = (tab) => {
    setActiveWorkoutTab(tab);
    localStorage.setItem('workout_structure_last_tab', tab);
  };

  const getCurrentWorkoutExercises = () => {
    return workoutStructure[activeWorkoutTab] || [];
  };

  const addWorkoutExercise = () => {
    if (!newExerciseInput.trim()) return;
    const updatedStructure = { ...workoutStructure };
    updatedStructure[activeWorkoutTab] = [...getCurrentWorkoutExercises(), newExerciseInput.trim()];
    updateData({
      fitness: { ...data.fitness, workoutStructure: updatedStructure }
    });
    setNewExerciseInput('');
  };

  const removeWorkoutExercise = (index) => {
    const updatedStructure = { ...workoutStructure };
    updatedStructure[activeWorkoutTab] = getCurrentWorkoutExercises().filter((_, i) => i !== index);
    updateData({
      fitness: { ...data.fitness, workoutStructure: updatedStructure }
    });
  };

  const addSport = () => {
    if (!newSport.name) return;

    const sportTemplate = predefinedSports.find(s => s.name === newSport.name);
    
    const sport = {
      id: Date.now().toString(),
      name: newSport.name,
      unit: sportTemplate?.unit || '',
      icon: sportTemplate?.icon || 'activity',
      startDate: newSport.startDate,
      createdAt: new Date().toISOString()
    };

    updateData({
      fitness: {
        ...data.fitness,
        sports: [...sports, sport]
      }
    });

    setNewSport({ name: '', startDate: '' });
    setShowAddSport(false);
  };

  const deleteSport = async (id) => {
    if (!(await confirm({ message: t('fitness.deleteSportConfirm'), danger: true }))) return;
    updateData({
      fitness: {
        ...data.fitness,
        sports: sports.filter(s => s.id !== id),
        personalBests: personalBests.filter(pb => pb.sportId !== id)
      }
    });
  };

  const updateSport = () => {
    if (!editingSport?.name) return;

    updateData({
      fitness: {
        ...data.fitness,
        sports: sports.map(s =>
          s.id === editingSport.id ? { ...s, ...editingSport } : s
        )
      }
    });

    setEditingSport(null);
  };

  const deleteRecord = async (id) => {
    if (!(await confirm({ message: t('fitness.deleteRecordConfirm'), danger: true }))) return;
    updateData({
      fitness: {
        ...data.fitness,
        personalBests: personalBests.filter(r => r.id !== id)
      }
    });
  };

  // Get best record for a sport
  const getBestRecord = (sportId) => {
    const sportRecords = personalBests.filter(r => r.sportId === sportId);
    if (sportRecords.length === 0) return null;
    return sportRecords.reduce((best, current) =>
      current.value > best.value ? current : best
    );
  };

  // Get all records for a sport sorted by date
  const getSportRecords = (sportId) => {
    return personalBests
      .filter(r => r.sportId === sportId)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('fitness.overviewTitle')}</h2>
          <p className="text-sm sm:text-base text-gray-500">{t('fitness.overviewSubtitle')}</p>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card bg-blue-50 border-blue-200">
          <p className="text-blue-600 text-sm font-medium">{t('fitness.totalSports')}</p>
          <p className="text-2xl font-bold text-blue-700">{sports.length}</p>
        </div>
        <div className="card bg-green-50 border-green-200">
          <p className="text-green-600 text-sm font-medium">{t('fitness.personalBests')}</p>
          <p className="text-2xl font-bold text-green-700">{personalBests.length}</p>
        </div>
        <div className="card bg-purple-50 border-purple-200">
          <p className="text-purple-600 text-sm font-medium">{t('fitness.activeSports')}</p>
          <p className="text-2xl font-bold text-purple-700">
            {sports.filter(s => getSportRecords(s.id).length > 0).length}
          </p>
        </div>
        <div className="card bg-orange-50 border-orange-200">
          <p className="text-orange-600 text-sm font-medium">{t('fitness.thisMonth')}</p>
          <p className="text-2xl font-bold text-orange-700">
            {personalBests.filter(pb => {
              const recordMonth = pb.date.slice(0, 7);
              const currentMonth = new Date().toISOString().slice(0, 7);
              return recordMonth === currentMonth;
            }).length}
          </p>
        </div>
      </div>

      {/* Sports Grid with Best Records */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('fitness.yourSports')}</h3>
        {sports.length === 0 ? (
          <div className="card text-center py-12">
            <Trophy size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-2">{t('fitness.noSports')}</p>
            <p className="text-sm text-gray-400">{t('fitness.noSportsHint')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sports.map((sport) => {
              const Icon = getSportIcon(sport.icon);
              const bestRecord = getBestRecord(sport.id);
              const recordCount = getSportRecords(sport.id).length;

              return (
                <div
                  key={sport.id}
                  onClick={() => setSelectedSport(sport)}
                  className="card hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-blue-100 text-blue-600 rounded-lg">
                      <Icon size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{sport.name}</h3>
                      <p className="text-sm text-gray-500">{t('fitness.recordsCount', { n: recordCount })}</p>
                    </div>
                  </div>

                  {bestRecord ? (
                    <div className="bg-green-50 rounded-lg p-3">
                      <p className="text-sm text-green-600 font-medium">{t('fitness.personalBest')}</p>
                      <p className="text-xl font-bold text-green-700">
                        {bestRecord.value} {sport.unit}
                      </p>
                      <p className="text-xs text-green-600">
                        {new Date(bestRecord.date).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">{t('fitness.noRecords')}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const renderSports = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('fitness.sportsTitle')}</h2>
          <p className="text-sm sm:text-base text-gray-500">{t('fitness.sportsSubtitle')}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowAddSport(true)}
            className="btn bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2 !px-3 !py-2 sm:!px-4"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">{t('fitness.addSport')}</span>
          </button>
        </div>
      </div>

      {/* Sports List */}
      {sports.length === 0 ? (
        <div className="card text-center py-12">
          <Trophy size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-4">{t('fitness.noSports')}</p>
          <button
            onClick={() => setShowAddSport(true)}
            className="btn bg-blue-600 text-white hover:bg-blue-700"
          >
            {t('fitness.addFirstSport')}
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {sports.map((sport) => {
            const Icon = getSportIcon(sport.icon);
            const records = getSportRecords(sport.id);
            const bestRecord = getBestRecord(sport.id);

            return (
              <div
                key={sport.id}
                onClick={() => setViewingSportDetail(sport)}
                className="card hover:shadow-lg transition-shadow cursor-pointer p-6"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-100 text-blue-600 rounded-xl">
                      <Icon size={40} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{sport.name}</h3>
                      {sport.startDate && (
                        <p className="text-sm text-gray-500">
                          {t('fitness.startedOn', { date: new Date(sport.startDate).toLocaleDateString('de-DE') })}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setEditingSport(sport)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-gray-100"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => deleteSport(sport.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Personal Best */}
                {bestRecord && (
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Trophy size={20} className="text-yellow-600" />
                      <span className="font-semibold text-yellow-800">{t('fitness.personalBest')}</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {bestRecord.value} {sport.unit}
                    </p>
                    <p className="text-sm text-gray-600">
                      {t('fitness.achievedOn', { date: new Date(bestRecord.date).toLocaleDateString('de-DE') })}
                      {bestRecord.notes && ` • ${bestRecord.notes}`}
                    </p>
                  </div>
                )}

                {/* Records History */}
                {records.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">{t('common.history')}</h4>
                    <div className="space-y-2 max-h-48 overflow-auto">
                      {records.map((record) => (
                        <div
                          key={record.id}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-gray-900">
                              {record.value} {sport.unit}
                            </span>
                            {bestRecord?.id === record.id && (
                              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                                {t('fitness.bestBadge')}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-500">
                              {new Date(record.date).toLocaleDateString('de-DE')}
                            </span>
                            {record.notes && (
                              <span className="text-sm text-gray-400">{record.notes}</span>
                            )}
                            <button
                              onClick={() => deleteRecord(record.id)}
                              className="p-1 text-gray-400 hover:text-red-600 rounded"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderSportDetail = () => {
    if (!viewingSportDetail) return null;
    
    const Icon = getSportIcon(viewingSportDetail.icon);
    const duration = calculateDuration(viewingSportDetail.startDate);
    
    return (
      <div className="space-y-6">
        {/* Back button */}
        <button
          onClick={() => setViewingSportDetail(null)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft size={20} />
          {t('fitness.backToSports')}
        </button>

        {/* Sport Header */}
        <div className="card">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-4 bg-blue-100 text-blue-600 rounded-xl">
              <Icon size={40} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{viewingSportDetail.name}</h1>
              {viewingSportDetail.startDate && (
                <p className="text-gray-500">
                  {t('fitness.startedOn', {
                    date: new Date(viewingSportDetail.startDate).toLocaleDateString('de-DE', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                  })}
                </p>
              )}
            </div>
          </div>

          {duration && (
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">{t('fitness.doingFor')}</p>
                  <p className="text-2xl font-bold text-gray-900">{duration}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">{t('fitness.totalWorkouts')}</p>
                  <p className="text-2xl font-bold text-gray-900">{currentWorkouts.length}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sports that support workout logging (Crossfit, Zumba) */}
        {['Crossfit', 'Zumba'].includes(viewingSportDetail.name) && (
          <div className="space-y-6">
            {/* Crossfit-only: Personal Records + Workout Structure */}
            {viewingSportDetail.name === 'Crossfit' && (
              <>
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">{t('fitness.personalRecords')}</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowWorkoutStructureManager(true)}
                  className="btn bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center gap-2"
                >
                  <Settings size={18} />
                  {t('fitness.workoutStructure')}
                </button>
                <button
                  onClick={() => setShowAddCrossfitRecord(true)}
                  className="btn bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
                >
                  <Plus size={18} />
                  {t('fitness.addRecord')}
                </button>
              </div>
            </div>

            {/* Lifts Section - only show if has records */}
            {crossfitExercises.lifts.some(ex => crossfitRecords[ex]) && (
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Dumbbell size={20} className="text-blue-600" />
                  {t('fitness.lifts')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {crossfitExercises.lifts.filter(ex => crossfitRecords[ex]).map((exercise) => {
                    const record = crossfitRecords[exercise];
                    return (
                      <div 
                        key={exercise} 
                        onClick={() => setEditingCrossfitRecord({ ...record, exercise })}
                        className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-900">{exercise}</p>
                          <Edit2 size={16} className="text-gray-400" />
                        </div>
                        <p className="text-sm text-green-600 font-semibold">
                          {record.weight && `${record.weight}kg`}
                          {record.weight && record.reps && ' × '}
                          {record.reps && t('fitness.reps', { n: record.reps })}
                          {record.time && record.time}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(record.date).toLocaleDateString('de-DE')}
                          {record.notes && ` • ${record.notes}`}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Gymnastics Section - only show if has records */}
            {crossfitExercises.gymnastics.some(ex => crossfitRecords[ex]) && (
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Activity size={20} className="text-purple-600" />
                  {t('fitness.gymnastics')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {crossfitExercises.gymnastics.filter(ex => crossfitRecords[ex]).map((exercise) => {
                    const record = crossfitRecords[exercise];
                    return (
                      <div 
                        key={exercise} 
                        onClick={() => setEditingCrossfitRecord({ ...record, exercise })}
                        className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-900">{exercise}</p>
                          <Edit2 size={16} className="text-gray-400" />
                        </div>
                        <p className="text-sm text-green-600 font-semibold">
                          {record.reps && t('fitness.reps', { n: record.reps })}
                          {record.time && record.time}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(record.date).toLocaleDateString('de-DE')}
                          {record.notes && ` • ${record.notes}`}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
              </>
            )}

            {/* Workouts Section (Crossfit + Zumba) */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <Calendar size={20} className="text-green-600" />
                  {t('fitness.workouts')}
                </h3>
                <div className="flex items-center gap-2 relative">
                  {/* Link to Calendar button */}
                  {(() => {
                    const cats = data.calendar?.categories || [];
                    const activeCat = cats.find((c) => c.id === currentCalendarCategoryId);
                    return (
                      <button
                        onClick={() => setShowCalendarLinker((v) => !v)}
                        className="btn flex items-center gap-2 text-sm px-3 py-1.5 border"
                        style={
                          activeCat
                            ? {
                                backgroundColor: `${activeCat.color}22`,
                                color: activeCat.color,
                                borderColor: `${activeCat.color}55`
                              }
                            : {
                                backgroundColor: '#f9fafb',
                                color: '#6b7280',
                                borderColor: '#e5e7eb'
                              }
                        }
                        title={activeCat ? t('fitness.syncedWith', { name: activeCat.name }) : t('fitness.linkToCalendar')}
                      >
                        <Calendar size={16} />
                        {activeCat ? activeCat.name : t('fitness.linkToCalendar')}
                      </button>
                    );
                  })()}
                  {showCalendarLinker && (
                    <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl p-3 z-20 w-72">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-700">
                          {t('fitness.labelFor', { name: currentSportName })}
                        </span>
                        <button
                          onClick={() => setShowCalendarLinker(false)}
                          className="p-0.5 hover:bg-gray-100 rounded"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {(() => {
                          const cats = data.calendar?.categories || [];
                          const sorted = [
                            ...cats.filter((c) => c.isCustom),
                            ...cats.filter((c) => !c.isCustom)
                          ];
                          return sorted.map((cat) => {
                            const selected = currentCalendarCategoryId === cat.id;
                            return (
                              <button
                                key={cat.id}
                                onClick={() => {
                                  setCurrentCalendarLink(cat.id);
                                  setShowCalendarLinker(false);
                                }}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                                  selected
                                    ? 'text-white ring-2 ring-offset-1'
                                    : cat.isCustom
                                    ? 'bg-gray-100 hover:bg-gray-200'
                                    : 'bg-gray-50 text-gray-400 hover:bg-gray-100 border border-dashed border-gray-300'
                                }`}
                                style={
                                  selected
                                    ? { backgroundColor: cat.color }
                                    : cat.isCustom
                                    ? { color: cat.color }
                                    : {}
                                }
                              >
                                <span
                                  className="w-2 h-2 rounded-full"
                                  style={{
                                    backgroundColor: selected
                                      ? '#fff'
                                      : cat.isCustom
                                      ? cat.color
                                      : '#d1d5db'
                                  }}
                                />
                                {cat.name}
                              </button>
                            );
                          });
                        })()}
                      </div>
                      {currentCalendarCategoryId && (
                        <button
                          onClick={() => {
                            setCurrentCalendarLink('');
                            setShowCalendarLinker(false);
                          }}
                          className="w-full text-xs text-red-600 hover:bg-red-50 rounded-lg py-1.5"
                        >
                          {t('fitness.unlink')}
                        </button>
                      )}
                    </div>
                  )}
                  <button
                    onClick={() => setShowAddWorkout(true)}
                    className="btn bg-green-600 text-white hover:bg-green-700 flex items-center gap-2 text-sm px-3 py-1.5"
                  >
                    <Plus size={16} />
                    {t('fitness.logWorkout')}
                  </button>
                </div>
              </div>
              
              {currentWorkouts.length === 0 ? (
                <p className="text-gray-500 text-center py-4">{t('fitness.noWorkouts')}</p>
              ) : (
                <div className="space-y-2">
                  {currentWorkouts.slice().sort((a, b) => new Date(b.date) - new Date(a.date)).map((workout) => (
                    <div 
                      key={workout.id} 
                      onClick={() => setViewingWorkout(workout)}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 text-green-600 rounded-lg">
                          <Dumbbell size={18} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            {workout.records && workout.records.length > 0 && (
                              <Star size={16} className="text-yellow-500 fill-yellow-500" />
                            )}
                            <p className="font-medium text-gray-900">
                              {new Date(workout.date).toLocaleDateString('de-DE', { 
                                weekday: 'short', 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {workout.notes && (
                              <FileText size={14} className="text-gray-400" />
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">{workout.duration}</p>
                        <p className="text-xs text-gray-400">{t('fitness.minutes')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Reset viewingSportDetail when switching away from sports view
  if (viewMode !== 'sports' && viewingSportDetail) {
    setViewingSportDetail(null);
  }

  return (
    <div className="space-y-6">
      {viewMode === 'sports' && viewingSportDetail ? renderSportDetail() : (
        viewMode === 'sports' ? renderSports() : renderOverview()
      )}

      {/* Add Sport Modal */}
      {showAddSport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('fitness.addNewSport')}</h3>
              <button
                onClick={() => { setShowAddSport(false); setNewSport({ name: '', startDate: '' }); }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <select
                className="input"
                value={newSport.name}
                onChange={(e) => setNewSport({ ...newSport, name: e.target.value })}
              >
                <option value="">{t('fitness.selectSport')}</option>
                {predefinedSports.map((sport) => (
                  <option key={sport.id} value={sport.name}>{sport.name}</option>
                ))}
              </select>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fitness.startedWhen')}
                </label>
                <input
                  type="date"
                  className="input"
                  value={newSport.startDate}
                  onChange={(e) => setNewSport({ ...newSport, startDate: e.target.value })}
                />
              </div>
              <div className="flex gap-2">
                <button onClick={addSport} className="btn bg-blue-600 text-white hover:bg-blue-700 flex-1">
                  {t('fitness.addSport')}
                </button>
                <button
                  onClick={() => { setShowAddSport(false); setNewSport({ name: '', startDate: '' }); }}
                  className="btn-secondary flex-1"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Sport Modal */}
      {editingSport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('fitness.editSport')}</h3>
              <button
                onClick={() => setEditingSport(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="text"
                placeholder={t('fitness.sportName')}
                className="input"
                value={editingSport.name}
                onChange={(e) => setEditingSport({ ...editingSport, name: e.target.value })}
                disabled
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fitness.startedWhen')}
                </label>
                <input
                  type="date"
                  className="input"
                  value={editingSport.startDate || ''}
                  onChange={(e) => setEditingSport({ ...editingSport, startDate: e.target.value })}
                />
              </div>
              <div className="flex gap-2">
                <button onClick={updateSport} className="btn bg-blue-600 text-white hover:bg-blue-700 flex-1">
                  {t('savings.saveChanges')}
                </button>
                <button
                  onClick={() => setEditingSport(null)}
                  className="btn-secondary flex-1"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sport Detail Modal */}
      {selectedSport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-auto">
            {(() => {
              const Icon = getSportIcon(selectedSport.icon);
              const records = getSportRecords(selectedSport.id);
              const bestRecord = getBestRecord(selectedSport.id);

              return (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-blue-100 text-blue-600 rounded-lg">
                        <Icon size={32} />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{selectedSport.name}</h3>
                        <p className="text-gray-500">
                          {selectedSport.startDate ? t('fitness.startedOn', { date: new Date(selectedSport.startDate).toLocaleDateString('de-DE') }) : t('fitness.noStartDate')}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedSport(null)}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  {bestRecord && (
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-6 mb-6">
                      <div className="flex items-center gap-2 mb-2">
                        <Trophy size={28} className="text-yellow-600" />
                        <span className="text-lg font-semibold text-yellow-800">{t('fitness.personalBest')}</span>
                      </div>
                      <p className="text-4xl font-bold text-gray-900">
                        {bestRecord.value} {selectedSport.unit}
                      </p>
                      <p className="text-gray-600 mt-1">
                        {new Date(bestRecord.date).toLocaleDateString('de-DE', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                      {bestRecord.notes && (
                        <p className="text-sm text-gray-500 mt-2">{bestRecord.notes}</p>
                      )}
                    </div>
                  )}

                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">
                      {t('fitness.allRecords', { n: records.length })}
                    </h4>
                    {records.length > 0 ? (
                      <div className="space-y-2 max-h-80 overflow-auto">
                        {records.map((record) => (
                          <div
                            key={record.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <span className="font-medium text-gray-900">
                                {record.value} {selectedSport.unit}
                              </span>
                              {bestRecord?.id === record.id && (
                                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded font-medium">
                                  {t('fitness.pbBadge')}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-gray-500">
                                {new Date(record.date).toLocaleDateString('de-DE')}
                              </span>
                              {record.notes && (
                                <span className="text-sm text-gray-400">{record.notes}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-center py-8">
                        {t('fitness.noRecordsHint', { name: selectedSport.name })}
                      </p>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Add Workout Modal */}
      {showAddWorkout && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('fitness.logWorkout')}</h3>
              <button
                onClick={closeAddWorkoutModal}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.date')}</label>
                  <input
                    type="date"
                    className="input"
                    value={newWorkout.date}
                    onChange={(e) => setNewWorkout({ ...newWorkout, date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('fitness.startTime')}</label>
                  <input
                    type="time"
                    className="input"
                    value={newWorkout.startTime}
                    onChange={(e) => setNewWorkout({ ...newWorkout, startTime: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.durationMinutes')}</label>
                <input
                  type="number"
                  placeholder={t('fitness.durationPlaceholder')}
                  className="input"
                  value={newWorkout.duration}
                  onChange={(e) => setNewWorkout({ ...newWorkout, duration: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.notesOptional')}</label>
                <input
                  type="text"
                  placeholder={t('fitness.notesPlaceholder')}
                  className="input"
                  value={newWorkout.notes}
                  onChange={(e) => setNewWorkout({ ...newWorkout, notes: e.target.value })}
                />
              </div>

              {/* Workout Structure (Crossfit only) */}
              {currentSportName === 'Crossfit' && (
              <div className="border-t pt-4 mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">{t('fitness.workoutStructure')}</label>
                
                {['warmup', 'strength', 'workoutOfMonth', 'wod'].map((section) => {
                  const sectionExercises = workoutStructure[section] || [];
                  if (sectionExercises.length === 0) return null;
                  
                  const sectionLabels = {
                    warmup: t('fitness.sectionWarmup'),
                    strength: t('fitness.sectionStrength'),
                    workoutOfMonth: t('fitness.sectionWorkoutOfMonth'),
                    wod: t('fitness.sectionWod')
                  };
                  
                  const wodType = newWorkout.wodType || '';
                  const resultPlaceholder = {
                    'For Time': t('fitness.resultForTime'),
                    'AMRAP': t('fitness.resultAmrap'),
                    'EMOM': t('fitness.resultEmom'),
                    'Tabata': t('fitness.resultTabata'),
                    'Other': t('fitness.resultOther')
                  };

                  return (
                    <div key={section} className="mb-3">
                      <label className="text-xs font-medium text-gray-500 uppercase mb-1 block">
                        {sectionLabels[section]}
                      </label>
                      <div className="flex gap-2">
                        <select
                          className="input text-sm flex-1"
                          value={newWorkout[section] || ''}
                          onChange={(e) => setNewWorkout({ ...newWorkout, [section]: e.target.value })}
                        >
                          <option value="">{t('fitness.selectExerciseEllipsis')}</option>
                          {sectionExercises.map((exercise, idx) => (
                            <option key={idx} value={exercise}>{exercise}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          placeholder={t('fitness.detailsPlaceholder')}
                          className="input text-sm w-24"
                          value={newWorkout[`${section}Details`] || ''}
                          onChange={(e) => setNewWorkout({ ...newWorkout, [`${section}Details`]: e.target.value })}
                        />
                      </div>
                      {section === 'wod' && (
                        <div className="flex gap-2 mt-2">
                          <select
                            className="input text-sm flex-1"
                            value={wodType}
                            onChange={(e) => setNewWorkout({ ...newWorkout, wodType: e.target.value, wodResult: '' })}
                          >
                            <option value="">{t('fitness.workoutType')}</option>
                            <option value="For Time">For Time</option>
                            <option value="AMRAP">AMRAP</option>
                            <option value="EMOM">EMOM</option>
                            <option value="Tabata">Tabata</option>
                            <option value="Other">{t('fitness.wodOther')}</option>
                          </select>
                          {wodType && (
                            <input
                              type="text"
                              placeholder={resultPlaceholder[wodType]}
                              className="input text-sm flex-1"
                              value={newWorkout.wodResult || ''}
                              onChange={(e) => setNewWorkout({ ...newWorkout, wodResult: e.target.value })}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              )}

              {/* Personal Records for this workout (Crossfit only) */}
              {currentSportName === 'Crossfit' && (
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">{t('fitness.personalRecords')}</label>
                  <button
                    onClick={() => setShowAddWorkoutRecord(true)}
                    className="p-1.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                    title={t('fitness.addRecord')}
                  >
                    <Plus size={18} />
                  </button>
                </div>

                {showAddWorkoutRecord && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-3 space-y-2">
                    <select
                      className="input text-sm"
                      value={newWorkoutRecord.exercise}
                      onChange={(e) => setNewWorkoutRecord({ ...newWorkoutRecord, exercise: e.target.value })}
                    >
                      <option value="">{t('fitness.selectExercise')}</option>
                      <optgroup label={t('fitness.lifts')}>
                        {crossfitExercises.lifts.map((ex) => (
                          <option key={ex} value={ex}>{ex}</option>
                        ))}
                      </optgroup>
                      <optgroup label={t('fitness.gymnasticsShort')}>
                        {crossfitExercises.gymnastics.map((ex) => (
                          <option key={ex} value={ex}>{ex}</option>
                        ))}
                      </optgroup>
                    </select>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        step="0.1"
                        placeholder={t('fitness.weightKg')}
                        className="input text-sm"
                        value={newWorkoutRecord.weight}
                        onChange={(e) => setNewWorkoutRecord({ ...newWorkoutRecord, weight: e.target.value })}
                      />
                      <input
                        type="number"
                        placeholder={t('fitness.repsLabel')}
                        className="input text-sm"
                        value={newWorkoutRecord.reps}
                        onChange={(e) => setNewWorkoutRecord({ ...newWorkoutRecord, reps: e.target.value })}
                      />
                    </div>
                    <input
                      type="text"
                      placeholder={t('fitness.timePlaceholder')}
                      className="input text-sm"
                      value={newWorkoutRecord.time}
                      onChange={(e) => setNewWorkoutRecord({ ...newWorkoutRecord, time: e.target.value })}
                    />
                    <input
                      type="text"
                      placeholder={t('common.notesOptional')}
                      className="input text-sm"
                      value={newWorkoutRecord.notes}
                      onChange={(e) => setNewWorkoutRecord({ ...newWorkoutRecord, notes: e.target.value })}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          if (!newWorkoutRecord.exercise) return;
                          setWorkoutRecords([...workoutRecords, { ...newWorkoutRecord, id: Date.now().toString() }]);
                          setNewWorkoutRecord({ exercise: '', weight: '', reps: '', time: '', notes: '' });
                          setShowAddWorkoutRecord(false);
                        }}
                        className="btn bg-blue-600 text-white hover:bg-blue-700 text-sm py-1.5 flex-1"
                      >
                        {t('common.add')}
                      </button>
                      <button
                        onClick={() => {
                          setNewWorkoutRecord({ exercise: '', weight: '', reps: '', time: '', notes: '' });
                          setShowAddWorkoutRecord(false);
                        }}
                        className="btn-secondary text-sm py-1.5 flex-1"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                )}

                {workoutRecords.length > 0 && (
                  <div className="space-y-2">
                    {workoutRecords.map((record) => (
                      <div key={record.id} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg text-sm">
                        <div>
                          <p className="font-medium text-gray-900">{record.exercise}</p>
                          <p className="text-blue-600">
                            {record.weight && `${record.weight}kg`}
                            {record.weight && record.reps && ' × '}
                            {record.reps && t('fitness.reps', { n: record.reps })}
                            {record.time && record.time}
                            {record.notes && ` • ${record.notes}`}
                          </p>
                        </div>
                        <button
                          onClick={() => setWorkoutRecords(workoutRecords.filter(r => r.id !== record.id))}
                          className="p-1 text-red-400 hover:text-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (!newWorkout.duration) return;

                    const workoutId = Date.now().toString();
                    let linkedEventId = null;
                    let updatedCalendarEvents = data.calendar?.events || [];

                    // Auto-sync to calendar if the sport is linked at page level
                    if (currentCalendarCategoryId) {
                      const cat = (data.calendar?.categories || []).find(
                        (c) => c.id === currentCalendarCategoryId
                      );
                      if (cat) {
                        const workoutForEvent = { ...newWorkout, id: workoutId };
                        const event = buildCalendarEventForWorkout(
                          workoutForEvent,
                          currentCalendarCategoryId,
                          currentSportName
                        );
                        linkedEventId = event.id;
                        updatedCalendarEvents = [...updatedCalendarEvents, event];
                      }
                    }

                    const workout = {
                      ...newWorkout,
                      id: workoutId,
                      createdAt: new Date().toISOString(),
                      records: workoutRecords,
                      linkedCalendarEventId: linkedEventId
                    };

                    const updatedWorkouts = [...currentWorkouts, workout];

                    const extraFitness = {};
                    if (workoutRecords.length > 0 && currentSportName === 'Crossfit') {
                      const updatedRecords = { ...crossfitRecords };
                      workoutRecords.forEach((record) => {
                        updatedRecords[record.exercise] = {
                          ...record,
                          date: newWorkout.date
                        };
                      });
                      extraFitness.crossfitRecords = updatedRecords;
                    }

                    persistCurrentWorkouts(updatedWorkouts, {
                      extraFitness,
                      calendar: linkedEventId
                        ? { ...data.calendar, events: updatedCalendarEvents }
                        : null
                    });

                    closeAddWorkoutModal();
                  }}
                  className="btn bg-green-600 text-white hover:bg-green-700 flex-1"
                >
                  {t('fitness.logWorkout')}
                </button>
                <button
                  onClick={closeAddWorkoutModal}
                  className="btn-secondary flex-1"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View/Edit Workout Modal */}
      {viewingWorkout && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {viewingWorkout.records && viewingWorkout.records.length > 0 && (
                  <Star size={20} className="text-yellow-500 fill-yellow-500" />
                )}
                <h3 className="text-lg font-semibold">
                  {new Date(viewingWorkout.date).toLocaleDateString('de-DE', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </h3>
              </div>
              <button
                onClick={() => setViewingWorkout(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Editable Date + Start Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.date')}</label>
                  <input
                    type="date"
                    className="input"
                    value={viewingWorkout.date}
                    onChange={(e) => updateViewingWorkout({ date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('fitness.startTime')}</label>
                  <input
                    type="time"
                    className="input"
                    value={viewingWorkout.startTime || ''}
                    onChange={(e) => updateViewingWorkout({ startTime: e.target.value })}
                  />
                </div>
              </div>

              {/* Editable Duration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.durationMinutes')}</label>
                <input
                  type="number"
                  className="input"
                  value={viewingWorkout.duration}
                  onChange={(e) => updateViewingWorkout({ duration: e.target.value })}
                />
              </div>
              
              {/* Editable Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.notes')}</label>
                <input
                  type="text"
                  className="input"
                  value={viewingWorkout.notes || ''}
                  onChange={(e) => updateViewingWorkout({ notes: e.target.value })}
                />
              </div>

              {/* Workout Structure - View Only */}
              {(['warmup', 'strength', 'workoutOfMonth', 'wod'].some(section => viewingWorkout[section])) && (
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-blue-700 mb-2">{t('fitness.workoutStructure')}</p>
                  {['warmup', 'strength', 'workoutOfMonth', 'wod'].map((section) => {
                    if (!viewingWorkout[section]) return null;
                    const sectionLabels = {
                      warmup: t('fitness.sectionWarmup'),
                      strength: t('fitness.sectionStrength'),
                      workoutOfMonth: t('fitness.sectionWorkoutOfMonth'),
                      wod: t('fitness.sectionWod')
                    };
                    return (
                      <div key={section} className="mb-2 last:mb-0">
                        <span className="text-xs font-medium text-blue-600 uppercase">{sectionLabels[section]}:</span>
                        <span className="text-sm text-gray-800 ml-2">
                          {viewingWorkout[`${section}Details`] ? `${viewingWorkout[`${section}Details`]} - ` : ''}
                          {viewingWorkout[section]}
                        </span>
                        {section === 'wod' && viewingWorkout.wodType && (
                          <div className="ml-2 mt-1 flex items-center gap-2">
                            <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded font-medium">
                              {viewingWorkout.wodType}
                            </span>
                            {viewingWorkout.wodResult && (
                              <span className="text-sm font-semibold text-gray-900">
                                {viewingWorkout.wodResult}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {viewingWorkout.records && viewingWorkout.records.length > 0 && (
                <div className="bg-yellow-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-yellow-700 mb-2">
                    <Trophy size={16} />
                    <span className="font-medium text-sm">{t('fitness.personalRecords')}</span>
                  </div>
                  <div className="space-y-2">
                    {viewingWorkout.records.map((record) => (
                      <div key={record.id} className="bg-white rounded-lg p-2">
                        <p className="font-medium text-gray-900">{record.exercise}</p>
                        <p className="text-sm text-gray-600">
                          {record.weight && `${record.weight}kg`}
                          {record.weight && record.reps && ' × '}
                          {record.reps && t('fitness.reps', { n: record.reps })}
                          {record.time && record.time}
                          {record.notes && ` • ${record.notes}`}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Delete Button */}
              <div className="pt-4 border-t">
                <button
                  onClick={() => {
                    const updatedWorkouts = currentWorkouts.filter(
                      (w) => w.id !== viewingWorkout.id
                    );
                    let calendarPayload = null;
                    if (viewingWorkout.linkedCalendarEventId) {
                      calendarPayload = {
                        ...data.calendar,
                        events: (data.calendar?.events || []).filter(
                          (ev) => ev.id !== viewingWorkout.linkedCalendarEventId
                        )
                      };
                    }
                    persistCurrentWorkouts(updatedWorkouts, { calendar: calendarPayload });
                    setViewingWorkout(null);
                  }}
                  className="btn bg-red-600 text-white hover:bg-red-700 w-full flex items-center justify-center gap-2"
                >
                  <Trash2 size={18} />
                  {t('fitness.deleteWorkout')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Workout Structure Manager Modal */}
      {showWorkoutStructureManager && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('fitness.structureManager')}</h3>
              <button
                onClick={() => setShowWorkoutStructureManager(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Tabs */}
            <div className="flex border-b mb-4">
              {[
                { id: 'warmup', label: t('fitness.sectionWarmup') },
                { id: 'strength', label: t('fitness.sectionStrength') },
                { id: 'workoutOfMonth', label: t('fitness.sectionWorkoutOfMonth') },
                { id: 'wod', label: t('fitness.sectionWod') }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleWorkoutTabChange(tab.id)}
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeWorkoutTab === tab.id
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            
            {/* Add Exercise */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                placeholder={t('fitness.exerciseNamePlaceholder')}
                className="input flex-1"
                value={newExerciseInput}
                onChange={(e) => setNewExerciseInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addWorkoutExercise()}
              />
              <button
                onClick={addWorkoutExercise}
                className="btn bg-blue-600 text-white hover:bg-blue-700"
              >
                <Plus size={18} />
              </button>
            </div>
            
            {/* Exercise List */}
            <div className="space-y-2">
              {getCurrentWorkoutExercises().length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  {t('fitness.noExercises', { section: activeWorkoutTab })}
                </p>
              ) : (
                getCurrentWorkoutExercises().map((exercise, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <span className="font-medium text-gray-900">{exercise}</span>
                    <button
                      onClick={() => removeWorkoutExercise(index)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Crossfit Record Modal */}
      {editingCrossfitRecord && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('fitness.editRecord', { name: editingCrossfitRecord.exercise })}</h3>
              <button
                onClick={() => setEditingCrossfitRecord(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  step="0.1"
                  placeholder={t('fitness.weightKg')}
                  className="input"
                  value={editingCrossfitRecord.weight || ''}
                  onChange={(e) => setEditingCrossfitRecord({ ...editingCrossfitRecord, weight: e.target.value })}
                />
                <input
                  type="number"
                  placeholder={t('fitness.repsLabel')}
                  className="input"
                  value={editingCrossfitRecord.reps || ''}
                  onChange={(e) => setEditingCrossfitRecord({ ...editingCrossfitRecord, reps: e.target.value })}
                />
              </div>
              <input
                type="text"
                placeholder={t('fitness.timePlaceholder')}
                className="input"
                value={editingCrossfitRecord.time || ''}
                onChange={(e) => setEditingCrossfitRecord({ ...editingCrossfitRecord, time: e.target.value })}
              />
              <input
                type="date"
                className="input"
                value={editingCrossfitRecord.date ? editingCrossfitRecord.date.split('T')[0] : new Date().toISOString().split('T')[0]}
                onChange={(e) => setEditingCrossfitRecord({ ...editingCrossfitRecord, date: e.target.value })}
              />
              <input
                type="text"
                placeholder={t('common.notesOptional')}
                className="input"
                value={editingCrossfitRecord.notes || ''}
                onChange={(e) => setEditingCrossfitRecord({ ...editingCrossfitRecord, notes: e.target.value })}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const updatedRecords = { ...crossfitRecords };
                    updatedRecords[editingCrossfitRecord.exercise] = {
                      ...editingCrossfitRecord,
                      id: editingCrossfitRecord.id || Date.now().toString()
                    };
                    updateData({
                      fitness: {
                        ...data.fitness,
                        crossfitRecords: updatedRecords
                      }
                    });
                    setEditingCrossfitRecord(null);
                  }}
                  className="btn bg-blue-600 text-white hover:bg-blue-700 flex-1"
                >
                  {t('savings.saveChanges')}
                </button>
                <button
                  onClick={() => setEditingCrossfitRecord(null)}
                  className="btn-secondary flex-1"
                >
                  {t('common.cancel')}
                </button>
              </div>
              <button
                onClick={() => {
                  const updatedRecords = { ...crossfitRecords };
                  delete updatedRecords[editingCrossfitRecord.exercise];
                  updateData({
                    fitness: {
                      ...data.fitness,
                      crossfitRecords: updatedRecords
                    }
                  });
                  setEditingCrossfitRecord(null);
                }}
                className="btn bg-red-600 text-white hover:bg-red-700 w-full flex items-center justify-center gap-2"
              >
                <Trash2 size={18} />
                {t('fitness.deleteRecord')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Crossfit Record Modal */}
      {showAddCrossfitRecord && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('fitness.addPersonalRecord')}</h3>
              <button
                onClick={() => {
                  setShowAddCrossfitRecord(false);
                  setNewCrossfitRecord({ exercise: '', weight: '', reps: '', time: '', notes: '', date: new Date().toISOString().split('T')[0] });
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              {(() => {
                const recordedExercises = Object.keys(crossfitRecords);
                const availableLifts = crossfitExercises.lifts.filter(ex => !recordedExercises.includes(ex));
                const availableGymnastics = crossfitExercises.gymnastics.filter(ex => !recordedExercises.includes(ex));
                
                if (availableLifts.length === 0 && availableGymnastics.length === 0) {
                  return (
                    <p className="text-gray-500 text-center py-4">
                      {t('fitness.allExercisesRecorded')}
                    </p>
                  );
                }
                
                return (
                  <select
                    className="input"
                    value={newCrossfitRecord.exercise}
                    onChange={(e) => setNewCrossfitRecord({ ...newCrossfitRecord, exercise: e.target.value })}
                  >
                    <option value="">{t('fitness.selectExercise')}</option>
                    {availableLifts.length > 0 && (
                      <optgroup label={t('fitness.lifts')}>
                        {availableLifts.map((ex) => (
                          <option key={ex} value={ex}>{ex}</option>
                        ))}
                      </optgroup>
                    )}
                    {availableGymnastics.length > 0 && (
                      <optgroup label={t('fitness.gymnastics')}>
                        {availableGymnastics.map((ex) => (
                          <option key={ex} value={ex}>{ex}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                );
              })()}
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  step="0.1"
                  placeholder={t('fitness.weightKg')}
                  className="input"
                  value={newCrossfitRecord.weight}
                  onChange={(e) => setNewCrossfitRecord({ ...newCrossfitRecord, weight: e.target.value })}
                />
                <input
                  type="number"
                  placeholder={t('fitness.repsLabel')}
                  className="input"
                  value={newCrossfitRecord.reps}
                  onChange={(e) => setNewCrossfitRecord({ ...newCrossfitRecord, reps: e.target.value })}
                />
              </div>
              <input
                type="text"
                placeholder={t('fitness.timePlaceholder')}
                className="input"
                value={newCrossfitRecord.time}
                onChange={(e) => setNewCrossfitRecord({ ...newCrossfitRecord, time: e.target.value })}
              />
              <input
                type="date"
                className="input"
                value={newCrossfitRecord.date}
                onChange={(e) => setNewCrossfitRecord({ ...newCrossfitRecord, date: e.target.value })}
              />
              <input
                type="text"
                placeholder={t('common.notesOptional')}
                className="input"
                value={newCrossfitRecord.notes}
                onChange={(e) => setNewCrossfitRecord({ ...newCrossfitRecord, notes: e.target.value })}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (!newCrossfitRecord.exercise) return;
                    
                    const newRecord = {
                      ...newCrossfitRecord,
                      id: Date.now().toString(),
                      createdAt: new Date().toISOString()
                    };
                    
                    const updatedRecords = {
                      ...crossfitRecords,
                      [newCrossfitRecord.exercise]: newRecord
                    };

                    updateData({
                      fitness: {
                        ...data.fitness,
                        crossfitRecords: updatedRecords
                      }
                    });
                    
                    setShowAddCrossfitRecord(false);
                    setNewCrossfitRecord({
                      exercise: '',
                      weight: '',
                      reps: '',
                      time: '',
                      date: new Date().toISOString().split('T')[0],
                      notes: ''
                    });
                  }}
                  className="btn bg-blue-600 text-white hover:bg-blue-700 flex-1"
                >
                  {t('fitness.addRecord')}
                </button>
                <button
                  onClick={() => {
                    setShowAddCrossfitRecord(false);
                    setNewCrossfitRecord({ exercise: '', weight: '', reps: '', time: '', notes: '', date: new Date().toISOString().split('T')[0] });
                  }}
                  className="btn-secondary flex-1"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Fitness;
